import React, { useState, useEffect } from 'react';
import {
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/api';
import CarsForSaleImportConfig from '../../components/vehicles/CarsForSaleImportConfig';

interface ImportSource {
  id: string;
  name: string;
  description: string;
  connection_type: string;
  status: 'active' | 'not_configured' | 'not_available' | 'error';
  is_primary: boolean;
  last_sync: string | null;
  total_imported: number;
  supported: boolean;
}

interface ImportHistoryItem {
  id: string;
  source: string;
  source_name: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  vehicles_imported: number;
  vehicles_updated: number;
  vehicles_failed: number;
  started_at: string | null;
  completed_at: string | null;
  triggered_by: string | null;
  trigger_type: string | null;
}

const InventoryImportSettings: React.FC = () => {
  const [sources, setSources] = useState<ImportSource[]>([]);
  const [history, setHistory] = useState<ImportHistoryItem[]>([]);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isMockSources, setIsMockSources] = useState(false);
  const [isMockHistory, setIsMockHistory] = useState(false);

  useEffect(() => {
    fetchSources();
    fetchHistory();
  }, []);

  const fetchSources = async () => {
    try {
      setLoading(true);
      const data = await apiService.get<ImportSource[]>('/vehicles/import/carsforsale/sources');
      setSources(data || []);
      setIsMockSources(false);

      // Auto-select primary source or first configured source
      if (data && data.length > 0) {
        const primary = data.find((s) => s.is_primary);
        const configured = data.find((s) => s.status === 'active');
        setSelectedSource(primary?.id || configured?.id || data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch import sources:', error);
      // Set default sources if API fails
      setSources([
        {
          id: 'carsforsale',
          name: 'CarsForSale.com',
          description: 'Import vehicles from CarsForSale.com CSV/FTP feeds',
          connection_type: 'ftp',
          status: 'not_configured',
          is_primary: true,
          last_sync: null,
          total_imported: 0,
          supported: true,
        },
        {
          id: 'cargurus',
          name: 'CarGurus',
          description: 'Import vehicles from CarGurus API/Feed',
          connection_type: 'api',
          status: 'not_available',
          is_primary: false,
          last_sync: null,
          total_imported: 0,
          supported: false,
        },
      ]);
      setSelectedSource('carsforsale');
      setIsMockSources(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const data = await apiService.get<ImportHistoryItem[]>(
        '/vehicles/import/carsforsale/history?limit=10'
      );
      setHistory(data || []);
      setIsMockHistory(false);
    } catch (error) {
      console.error('Failed to fetch import history:', error);
      setHistory([
        {
          id: 'mock-import-1',
          source: 'carsforsale',
          source_name: 'CarsForSale.com',
          status: 'COMPLETED',
          vehicles_imported: 42,
          vehicles_updated: 18,
          vehicles_failed: 0,
          started_at: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
          completed_at: new Date(Date.now() - 1000 * 60 * 60 * 5.5).toISOString(),
          triggered_by: 'System',
          trigger_type: 'scheduled',
        },
        {
          id: 'mock-import-2',
          source: 'carsforsale',
          source_name: 'CarsForSale.com',
          status: 'FAILED',
          vehicles_imported: 0,
          vehicles_updated: 0,
          vehicles_failed: 4,
          started_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
          completed_at: new Date(Date.now() - 1000 * 60 * 60 * 23.5).toISOString(),
          triggered_by: 'Admin User',
          trigger_type: 'manual',
        },
      ]);
      setIsMockHistory(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Active
          </span>
        );
      case 'not_configured':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Cog6ToothIcon className="w-3 h-3 mr-1" />
            Not Configured
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
            Error
          </span>
        );
      case 'not_available':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            Planned
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Planned
          </span>
        );
    }
  };

  const getImportStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Completed
          </span>
        );
      case 'FAILED':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Failed
          </span>
        );
      case 'IN_PROGRESS':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
            In Progress
          </span>
        );
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {status}
          </span>
        );
    }
  };

  const renderSourceConfig = () => {
    if (!selectedSource) return null;

    switch (selectedSource) {
      case 'carsforsale':
        return <CarsForSaleImportConfig />;
      case 'cargurus':
        return <CarGurusPlaceholder />;
      default:
        return (
          <ComingSoonPlaceholder
            sourceName={sources.find((s) => s.id === selectedSource)?.name || 'Import Source'}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Import Settings</h2>
        <p className="text-gray-600">
          Configure inventory import sources to automatically sync vehicles from external listing
          services.
        </p>
      </div>

      {/* Import Sources Grid */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Available Import Sources</h3>
          {isMockSources && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Demo data
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {sources.map((source) => (
            <button
              key={source.id}
              onClick={() => source.supported && setSelectedSource(source.id)}
              disabled={!source.supported}
              className={`relative p-4 border-2 rounded-xl text-left transition-all ${
                selectedSource === source.id
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : source.supported
                    ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
              }`}
            >
              {source.is_primary && (
                <div className="absolute -top-2 -right-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">
                    Primary
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{source.name}</h4>
                {selectedSource === source.id && (
                  <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                )}
              </div>

              <p className="text-xs text-gray-500 mb-3">{source.description}</p>

              <div className="flex items-center justify-between">
                {getStatusBadge(source.status)}
                {source.total_imported > 0 && (
                  <span className="text-xs text-gray-500">{source.total_imported} imported</span>
                )}
              </div>

              {source.last_sync && (
                <p className="text-xs text-gray-400 mt-2">
                  Last sync: {new Date(source.last_sync).toLocaleDateString()}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Selected Source Configuration */}
      {selectedSource && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Configure {sources.find((s) => s.id === selectedSource)?.name}
            </h3>
            <button
              onClick={fetchSources}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              <ArrowPathIcon className="w-4 h-4 mr-1" />
              Refresh
            </button>
          </div>

          {renderSourceConfig()}
        </div>
      )}

      {/* Import History */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Import History</h3>
          {isMockHistory && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Demo data
            </span>
          )}
          <button
            onClick={fetchHistory}
            disabled={historyLoading}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-1 ${historyLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {history.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CloudArrowUpIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No import history yet.</p>
            <p className="text-sm">Import history will appear here after your first import.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Imported
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Failed
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Triggered By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.started_at ? new Date(item.started_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.source_name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getImportStatusBadge(item.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      +{item.vehicles_imported}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-600">
                      {item.vehicles_updated}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600">
                      {item.vehicles_failed > 0 ? item.vehicles_failed : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.triggered_by || '-'}
                      {item.trigger_type && (
                        <span className="text-xs text-gray-400 ml-1">({item.trigger_type})</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button className="text-blue-600 hover:text-blue-800 flex items-center">
                        Details
                        <ChevronRightIcon className="w-4 h-4 ml-1" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Placeholder components for unsupported sources
const CarGurusPlaceholder: React.FC = () => (
  <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-6">
    <div className="flex items-start gap-4">
      <CloudArrowUpIcon className="w-12 h-12 text-gray-400" />
      <div>
        <h3 className="text-lg font-medium text-gray-900">CarGurus Integration (Beta Preview)</h3>
        <p className="text-sm text-gray-600 mt-1">
          Live sync is not enabled yet. The panel below outlines the expected setup steps and data
          flow for beta readiness.
        </p>
      </div>
    </div>

    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Readiness Checklist</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Dealer portal credentials verified</li>
          <li>• API feed enabled by CarGurus support</li>
          <li>• Listing permissions granted</li>
          <li>• Schedule agreed (daily sync)</li>
        </ul>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Planned Data Flow</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Inventory: nightly full refresh</li>
          <li>• Pricing: near-real-time updates</li>
          <li>• Photos: batch import + diff updates</li>
          <li>• Status: availability + merchandising flags</li>
        </ul>
      </div>
    </div>

    <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
      <strong>Next step:</strong> add CarGurus credentials in Marketplace Integrations when enabled.
    </div>
  </div>
);

const ComingSoonPlaceholder: React.FC<{ sourceName: string }> = ({ sourceName }) => (
  <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-6">
    <div className="flex items-start gap-4">
      <ClockIcon className="w-12 h-12 text-gray-400" />
      <div>
        <h3 className="text-lg font-medium text-gray-900">{sourceName} Integration (Planned)</h3>
        <p className="text-sm text-gray-600 mt-1">
          This integration is queued. Use the checklist below to prepare for activation.
        </p>
      </div>
    </div>

    <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-2">Preparation Checklist</h4>
      <ul className="text-sm text-gray-600 space-y-1">
        <li>• Confirm dealer account access</li>
        <li>• Identify feed format (API, CSV, FTP)</li>
        <li>• Map required inventory fields</li>
        <li>• Decide sync cadence</li>
      </ul>
    </div>
  </div>
);

export default InventoryImportSettings;
